/**
 * The Markets overlay (M1.1): one card per category — demand, the spot index
 * and its floor, what you sold and stocked, your share, the buyers' weights
 * over the category's dimension vector (M1.4 — era-drifting, decomposed in
 * the tooltip), the warehouse overhang the index reads, the open socket with
 * its bid form, and the running contracts.
 *
 * The price sparkline is display telemetry, like the egui shell's
 * `price_history`: a client-side ring of the last sixty weekly index samples
 * per category, kept in module state as ticks arrive. It never crosses back
 * into the sim and is not saved — reload and the line starts over.
 */

import { useState, useSyncExternalStore } from 'react';
import type { FloorHost, MarketUi } from '../bridge';
import { int, money, pctOfMille, timesOfMille } from './format';
import { openHandbook, setPanels, usePanels } from './panels';
import { snapshot, subscribe } from './store';
import { Tip, atomic } from './Tooltip';

/* ------------------------------------------------------------------ *
 * Price telemetry (client-side, per week)
 * ------------------------------------------------------------------ */

const HISTORY = 60;
const priceHistory = new Map<string, number[]>();
let sampledWeek = -1;

subscribe(() => {
  const c = snapshot().campaign;
  if (!c || c.week === sampledWeek) return;
  sampledWeek = c.week;
  for (const m of c.markets) {
    const series = priceHistory.get(m.id) ?? [];
    series.push(m.price);
    if (series.length > HISTORY) series.splice(0, series.length - HISTORY);
    priceHistory.set(m.id, series);
  }
});

function Sparkline({ series }: { series: number[] }) {
  const w = 120;
  const h = 22;
  if (series.length < 2) return <span className="spark muted">—</span>;
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of series) {
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  const span = hi - lo || 1;
  const points = series
    .map((v, i) => `${((i / (series.length - 1)) * (w - 2) + 1).toFixed(1)},${(h - 1 - ((v - lo) / span) * (h - 2)).toFixed(1)}`)
    .join(' ');
  return (
    <svg className="spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <title>{`spot index, last ${series.length} weeks: ${money(lo)} – ${money(hi)}`}</title>
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1" />
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * The overlay
 * ------------------------------------------------------------------ */

export function Markets({ host }: { host: FloorHost }) {
  const hud = useSyncExternalStore(subscribe, snapshot);
  const panels = usePanels();
  const c = hud.campaign;
  if (panels.economy !== 'markets' || !c) return null;
  return (
    <div className="overlay markets">
      <header>
        <h2>Markets</h2>
        <span className="muted">
          ambient reference: frontier <strong>{c.tech.frontierName}</strong> — offers are judged on each category's composite index against it
        </span>
        <button className="help" title="the handbook: markets" onClick={() => openHandbook('markets')}>
          ?
        </button>
        <button className="close" title="close" onClick={() => setPanels({ economy: null })}>
          ×
        </button>
      </header>
      {c.demandMille !== 1000 && (
        <div className="hint warn">⚑ macro overlay: demand at {pctOfMille(c.demandMille, 0)} this week</div>
      )}
      <div className="grid">
        {c.markets.map((m) => (
          <MarketCard key={m.id} market={m} firm={c.firm.id} host={host} running={c.run === 'running'} />
        ))}
      </div>
    </div>
  );
}

function MarketCard({ market: m, firm, host, running }: { market: MarketUi; firm: string; host: FloorHost; running: boolean }) {
  const [product, setProduct] = useState('');
  const [bidMille, setBidMille] = useState(1000);
  const ignition = !m.ignited
    ? 'latent'
    : m.ignitionWeek == null || m.ignitionWeek <= 0
      ? 'ignited (pre-seed)'
      : `ignited wk ${m.ignitionWeek}`;
  const socket = m.socket;
  const eligible = m.eligibleProducts;
  const effProduct = eligible.includes(product) ? product : (eligible[0] ?? '');
  const mine = m.contracts.filter((k) => k.player);
  const others = m.contracts.filter((k) => !k.player);
  return (
    <div className="market">
      <div className="market-head">
        <strong>{m.name}</strong>
        <span className={`chip ${m.ignited ? 'ok' : ''}`}>{ignition}</span>
      </div>
      <table>
        <tbody>
          <tr>
            <td>demand</td>
            <td>
              <Tip
                figure={atomic(
                  'Demand',
                  `${int(m.demand)} units/wk`,
                  "Units the category's buyers want this week: the S-curve at this date, times the macro overlay from any active event.",
                )}
              >
                {int(m.demand)} units/wk
              </Tip>
            </td>
          </tr>
          <tr>
            <td>spot index</td>
            <td>
              <Tip
                figure={atomic(
                  'Spot index',
                  money(m.price),
                  `The category's reference unit price this week — the cycle, in one number. Floors at ${money(m.floorPrice)}. Your asking price is a multiple of it.`,
                )}
              >
                {money(m.price)}
              </Tip>{' '}
              <span className="muted">floor {money(m.floorPrice)}</span>
            </td>
          </tr>
          <tr>
            <td>history</td>
            <td>
              <Sparkline series={priceHistory.get(m.id) ?? []} />
            </td>
          </tr>
          <tr>
            <td>you sold / unsold</td>
            <td>
              <Tip
                figure={atomic(
                  'Sold',
                  `${int(m.playerSold)} units`,
                  'Units of yours the logit awarded last week, out of what your lines produced for this category.',
                )}
              >
                {int(m.playerSold)}
              </Tip>
              {' / '}
              <Tip
                figure={atomic(
                  'Unsold',
                  `${int(m.playerUnsold)} units`,
                  m.hasInventory
                    ? 'Units offered and not sold last week — into the warehouse, at a carrying cost, sellable later (M1.4).'
                    : 'Units produced and not sold last week — scrapped; this category carries no inventory.',
                )}
              >
                <span className={m.playerUnsold > 0 ? 'warn' : ''}>{int(m.playerUnsold)}</span>
              </Tip>
            </td>
          </tr>
          <tr>
            <td>revenue</td>
            <td>
              <Tip figure={atomic('Revenue', money(m.playerRevenue), 'Your units sold × your asking price, last week — the line the ledger books under this category.')}>
                {money(m.playerRevenue)}
              </Tip>
            </td>
          </tr>
          <tr>
            <td>share</td>
            <td>
              <Tip
                figure={atomic('Share', pctOfMille(m.playerShareMille), "share = your units sold / the category's demand, last week.")}
              >
                {pctOfMille(m.playerShareMille)}
              </Tip>{' '}
              <span className="muted">· ambient sold {int(m.ambientSold)}</span>
            </td>
          </tr>
          {m.hasInventory && (
            <tr>
              <td>stock / overhang</td>
              <td>
                <Tip
                  figure={atomic(
                    'Your stock',
                    `${int(m.playerStock)} units`,
                    'Finished units of yours in the warehouse: unsold weeks past, offered again every week, carried at cost. Their spec is frozen while the frontier moves — obsolescence is the market improving around them.',
                  )}
                >
                  {int(m.playerStock)}
                </Tip>{' '}
                <Tip
                  figure={atomic(
                    'Overhang',
                    pctOfMille(m.overhangMille),
                    "Everyone's stock against weekly demand — the warehouse the price index reads beside the week's imbalance. A glut deepens while it drains; a shortage is met from it before prices ratchet.",
                  )}
                >
                  <span className="muted">· overhang {pctOfMille(m.overhangMille, 0)} of a week</span>
                </Tip>
              </td>
            </tr>
          )}
          <tr>
            <td>buyers weigh</td>
            <td>
              <Tip figure={m.weightsFigure}>
                {m.dimensions.map((d) => `${d.name.toLowerCase()} ${pctOfMille(d.weightMille, 0)}`).join(' · ')} · price{' '}
                {pctOfMille(m.priceWeightMille, 0)}
              </Tip>
              <span className="muted"> · binning: {m.binning}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="socket">
        {socket ? (
          <>
            <div>
              <strong>socket #{socket.id}</strong>: {int(socket.unitsPerWeek)} units/wk × {socket.durationWeeks} wk · min composite{' '}
              {timesOfMille(socket.minVsAmbientMille)} ambient · closes in {socket.closesInWeeks} wk
              {socket.hasOwnWeights && <span className="muted"> · this buyer weighs its own vector</span>}
            </div>
            {socket.bids.length > 0 && (
              <div className="hint">
                bids:{' '}
                {socket.bids.map((b, i) => (
                  <span key={i} className={b.player ? 'ok' : ''}>
                    {i > 0 && ', '}
                    {b.firm} · {b.product} at {timesOfMille(b.priceMille)} idx
                  </span>
                ))}
              </div>
            )}
            {socket.bids.some((b) => b.player) && (
              <div className="hint ok">your bid is in — resolution scores it against the ambient industry</div>
            )}
            {eligible.length === 0 ? (
              <div className="hint">no design of yours meets the composite bar</div>
            ) : (
              <div className="form-row">
                <select value={effProduct} onChange={(e) => setProduct(e.target.value)}>
                  {eligible.map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
                <label>
                  price ‰{' '}
                  <input
                    type="number"
                    min={100}
                    max={10_000}
                    step={50}
                    value={bidMille}
                    onChange={(e) => setBidMille(Number(e.target.value))}
                  />
                </label>
                <span className="muted">{timesOfMille(bidMille)} idx</span>
                <button
                  className="small"
                  disabled={!running || !effProduct}
                  onClick={() =>
                    host.sendSim({
                      BidSocket: {
                        firm,
                        category: m.id,
                        product: effProduct,
                        price_mille: Math.max(100, Math.min(10_000, Math.round(bidMille))),
                      },
                    })
                  }
                >
                  Bid
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="hint">no socket out for bid</div>
        )}
      </div>

      {(mine.length > 0 || others.length > 0) && (
        <div className="contracts">
          <div className="muted">contracts</div>
          {mine.map((k, i) => (
            <div key={`me-${i}`} className="ok">
              you: {k.product} × {int(k.unitsPerWeek)} units/wk at {money(k.unitPrice)} — {k.weeksLeft} wk left
            </div>
          ))}
          {others.map((k, i) => (
            <div key={`o-${i}`} className="muted">
              {k.firm}: {k.product} × {int(k.unitsPerWeek)} units/wk at {money(k.unitPrice)} — {k.weeksLeft} wk left
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
